import React, {
  useEffect, useMemo, useState, useRef,
} from "react";

import { Button, Input } from "antd";
import { toast } from "react-toastify";
import { PlusOutlined } from "@ant-design/icons";
import QcRuleDetailsModal from "./QcRuleDetailsModal";
import EditQcRuleModal from "./EditQcRuleModal";
import { getMlQcRules } from "../../../../actions/qc";
import RuleDeleteConfirmation from "./RuleDeleteConfirmation";
import QcRulesTable from "./QcRulesTable";
import QcRuleTestModal from "../QcRuleTestModal";

import "./QcRules.css";

export default function QcRules() {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [mlQcRules, setMlQcRules] = useState([]);
  const [ruleData, setRuleData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteFromTable, setDeleteFromTable] = useState(false);
  const abortControllerRef = useRef(null);

  // Pagination state
  const defaultPageSize = 50;
  const [paginationConfig, setPaginationConfig] = useState({
    pageSize: defaultPageSize,
    total: 0,
    current: 1,
    pageSizeOptions: ["10", "25", "50", "100"],
  });

  /**
   * Fetch qc rules list
   * @param {string} search search text
   * @param {string} rule_id rule id
   * @param {boolean} preservePage whether to preserve current page
   */
  const fetchQcRules = async (search = "", rule_id = "", preservePage = false) => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const params = {
      search, rule_id,
    };
    setLoading(true);

    const response = await getMlQcRules(params, abortController.signal);
    if (response.success) {
      setMlQcRules(response.result);

      // Update pagination config with actual data length for client-side pagination
      setPaginationConfig((prevState) => ({
        ...prevState,
        total: response.result?.length || 0,
        current: preservePage ? prevState.current : 1,
      }));

      // if api call completed and abort controller has ref
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    } else {
      setMlQcRules([]);
      // don't show error message if request is intentionally aborted
      if (!abortController?.signal?.aborted) {
        toast.error(response.message || "Something went wrong!");
      }
    }
    setLoading(false);
  };

  /**
   * Handle page change
   */
  const handlePageChange = (page, pageSize) => {
    setPaginationConfig((prevState) => ({
      ...prevState,
      current: page,
      pageSize,
    }));
  };

  /**
   * Initial data fetching
   */
  useEffect(() => {
    fetchQcRules();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Handles row click for table
   * @param {*} data row data
   */
  const onRowClick = (data) => {
    setRuleData(data);
    setShowDetailsModal(true);
  };

  /**
   * Closing all modals
   */
  const onCloseModals = () => {
    setDeleteConfirmation(false);
    setShowEditModal(false);
    setShowDetailsModal(false);
    setRuleData(null);
  };

  /**
   * Showing edit modal
   * @param {*} record row data
   */
  const onEdit = (record) => {
    setRuleData(record);
    setShowEditModal(true);
  };

  /**
   * Showing delete modal
   * @param {*} record row data
   */
  const onDelete = (record) => {
    setDeleteFromTable(true); // If deleted using Delete button in Table
    setRuleData(record);
    setDeleteConfirmation(true);
  };

  const onTest = (record) => {
    setRuleData(record);
    setShowTestModal(true);
  };

  const onCloseTestModal = () => {
    setShowTestModal(false);
    setRuleData(null);
  };

  /**
   * Refetch with current page preservation
   */
  const refetchWithCurrentPage = () => {
    fetchQcRules(searchText, "", true);
  };

  /**
   * Delete confirmation modal confirm click
   */
  const onDeleteConfirmation = () => {
    onCloseModals();
    refetchWithCurrentPage();
  };

  /**
   * delete confirmation modal cancel click
   */
  const onDeleteConfirmationCancel = () => {
    if (deleteFromTable) {
      setRuleData(null);
      setDeleteFromTable(false);
    }
    setDeleteConfirmation(false);
  };

  /**
   * on change event handler for search bar
   * @param {string} value search text
   */
  const handleOnSearchChange = (value) => {
    if (searchText.trim() !== "" && value === "") {
      // Remove duplicate setPaginationConfig call - let fetchQcRules handle it
      fetchQcRules(value, "");
    }
    setSearchText(value);
  };

  /**
   * on search handler for search bar
   * @param {string} value search text
   */
  const handleSearchText = async (value) => {
    const trimmedValue = (value || "").trim();
    // Remove duplicate setPaginationConfig call - let fetchQcRules handle it
    await fetchQcRules(trimmedValue, "");
  };

  const dataSource = useMemo(() => ((mlQcRules && mlQcRules.length) ? mlQcRules : []), [mlQcRules]);

  return (
    <>
      <div className="QcRules_Container">
        <header>
          <Input.Search
            className="QcRules_Search"
            placeholder="Search by Type/Description"
            value={searchText}
            onChange={(e) => handleOnSearchChange(e.target.value)}
            onSearch={handleSearchText}
            allowClear
          />
          <Button
            type="primary"
            htmlType="button"
            onClick={() => setShowEditModal(true)}
          >
            <PlusOutlined />
            <span>New Rule</span>
          </Button>
        </header>
        <div className="QcRules_TableContainer">
          <QcRulesTable
            dataSource={dataSource}
            loading={loading}
            onRowClick={onRowClick}
            onEdit={onEdit}
            onDelete={onDelete}
            onTest={onTest}
            pagination={{ ...paginationConfig, onChange: handlePageChange }}
          />
        </div>
      </div>
      {showEditModal && (
        <EditQcRuleModal
          open={showEditModal}
          onCancel={onCloseModals}
          editData={ruleData}
          refetch={refetchWithCurrentPage}
        />
      )}
      {showDetailsModal && (
        <QcRuleDetailsModal
          open={showDetailsModal}
          ruleData={ruleData}
          onCancel={onCloseModals}
          refetch={refetchWithCurrentPage}
          onDelete={() => setDeleteConfirmation(true)}
        />
      )}
      {deleteConfirmation && (
        <RuleDeleteConfirmation
          data={ruleData}
          onConfirm={onDeleteConfirmation}
          onCancel={onDeleteConfirmationCancel}
        />
      )}
      {showTestModal && (
        <QcRuleTestModal
          open={showTestModal}
          onCancel={onCloseTestModal}
          ruleData={ruleData}
          title="Test General Rule"
        />
      )}
    </>
  );
}
